import React, { useState } from "react";
import {
  ArrowUpRight, Lock, HeartHandshake, MessageSquareHeart, Stethoscope,
  FlaskConical, Users, BookOpen, EyeOff, FileLock2, ShieldCheck, Sparkles, Languages,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import AppointmentDialog from "@/components/AppointmentDialog";
import AnonymousEnquiry from "@/components/AnonymousEnquiry";
import { StoryCarousel } from "@/components/StoryCarousel";
import useSEO from "@/hooks/useSEO";
import { useLang } from "@/context/LangContext";

const shyFaqs = [
  { q: "Will the doctor judge me for what I say?", a: "No. We are psychiatrists — our entire training is built on listening without judgement. Whatever you describe, we have almost certainly heard it before from someone else sitting in the same chair." },
  { q: "Can I come without telling my family or my partner?", a: "Yes. You do not need anyone's permission or presence to consult. Nothing is disclosed to your family without your explicit consent." },
  { q: "Is my problem because I masturbated too much when I was young?", a: "Almost certainly not. There is no scientific evidence that masturbation causes erectile dysfunction, infertility, weakness or premature ejaculation. Guilt and anxiety about it, however, can absolutely cause sexual difficulty — and that is treatable." },
  { q: "I am unmarried. Can I still consult?", a: "Absolutely. Many of our patients are unmarried, and some come specifically to prepare for marriage with accurate information rather than street-corner myths." },
  { q: "Do I have to undergo a physical examination?", a: "Only if it is clinically necessary, and only with your consent. Many psychosexual conditions are diagnosed entirely from history. Where an examination or blood test adds value, we explain exactly why before proceeding." },
  { q: "My erection is fine when alone but fails with my partner. Is that physical or mental?", a: "That pattern strongly suggests a psychological or performance-anxiety component rather than a structural problem — which is good news, because it responds very well to sex therapy, with or without short-term medication." },
  { q: "We've been married for months and still could not have intercourse. Is that treatable?", a: "Yes. Unconsummated marriage is common and highly treatable — usually caused by vaginismus, performance anxiety, or lack of accurate sexual knowledge in one or both partners. Most couples improve within a few structured sessions." },
  { q: "Will I need medicines forever? Will I become dependent on them?", a: "In most psychosexual conditions medication is short-term, supportive, and used alongside therapy. We aim to taper it off. We do not prescribe endless 'courses' or unnecessary tonics." },
  { q: "Do you sell power capsules, oils or ayurvedic 'strength' medicines?", a: "No. We are a hospital, not a shop. We prescribe only evidence-based treatment, and we will tell you honestly when nothing needs to be prescribed at all." },
  { q: "What if my problem is embarrassing to say out loud?", a: "You can write it down and hand it to us — or use the anonymous enquiry form below. Many patients start that way and come in for a consultation afterwards." },
  { q: "Is a phone or video consultation possible?", a: "Yes, for follow-ups and for initial discussions when travelling to Bhavnagar is difficult. Some conditions do require an in-person assessment, and we will tell you if that applies to you." },
  { q: "What will it cost?", a: "A standard OPD consultation fee applies — the same as any other psychiatric consultation at Manas. There are no packages, no hidden charges and no pressure to 'commit' to a plan. Call the clinic for current fees." },
];

const HERO = "https://images.unsplash.com/photo-1604881991720-f91add269bed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxjb3VwbGUlMjBob2xkaW5nJTIwaGFuZHMlMjB3YXJtJTIwY2FsbSUyMGNvdW5zZWxsaW5nfGVufDB8fHx8MTc4ODE3OTAzNXww&ixlib=rb-4.1.0&q=85";

const groups = [
  {
    label: "Men's Sexual Health",
    items: [
      "Erectile Dysfunction",
      "Premature Ejaculation",
      "Delayed / Absent Ejaculation",
      "Performance Anxiety",
      "Dhat Syndrome (semen-loss anxiety)",
      "Nightfall & Masturbation Guilt",
      "Concerns about size & shape",
      "Low Sexual Desire in Men",
    ],
  },
  {
    label: "Women's Sexual Health",
    items: [
      "Low Libido / Desire Disorder",
      "Painful Intercourse (Dyspareunia)",
      "Vaginismus",
      "Difficulty Reaching Orgasm",
      "Arousal Difficulties",
      "Sexual Aversion Disorder",
      "Post-partum & Menopausal Changes",
      "Body-image related distress",
    ],
  },
  {
    label: "Couples & Relationship",
    items: [
      "Unconsummated Marriage",
      "Mismatched Desire between Partners",
      "Marital & Intimacy Discord",
      "Infertility-related Sexual Stress",
      "Communication & Trust Issues",
      "Sexless Relationships",
    ],
  },
  {
    label: "Complex & Medical",
    items: [
      "Sexual Problems in Diabetes & Hypertension",
      "Medication-induced Sexual Dysfunction",
      "Post-illness & Post-surgical Sexual Recovery",
      "Sexual Dysfunction with Depression / Anxiety",
      "Compulsive Sexual Behaviour & Porn Dependence",
      "Paraphilias & Impulse-control Concerns",
      "Gender & Sexual Identity Concerns",
      "Sexual Health in Addiction Recovery",
    ],
  },
];

const approach = [
  { icon: BookOpen, title: "Sex Education & Myth-Busting", desc: "Most distress we see is rooted in misinformation. We begin by replacing myths with medically accurate facts — calmly, without judgement." },
  { icon: MessageSquareHeart, title: "Sex Therapy & CBT", desc: "Structured behavioural techniques — sensate focus, stop-start, anxiety reduction and cognitive restructuring — delivered over planned sessions." },
  { icon: Users, title: "Couple Counselling", desc: "Where both partners are willing, therapy works faster. Sessions are conducted together or separately, always at your comfort." },
  { icon: Stethoscope, title: "Medical & Pharmacological Care", desc: "Where indicated, evidence-based medication is prescribed and reviewed by qualified psychiatrists — never sold as a 'course' or a package." },
  { icon: FlaskConical, title: "Laboratory & Hormonal Evaluation", desc: "Sugar, thyroid, testosterone, prolactin and lipid profiles when clinically relevant — because sexual symptoms often signal physical illness." },
  { icon: HeartHandshake, title: "Coordinated Referrals", desc: "When a urological, gynaecological or endocrine opinion is required, we coordinate it with trusted specialists in Bhavnagar." },
];

const promises = [
  { icon: EyeOff, label: "No names in the waiting area", desc: "You are called in privately. Nothing about your visit is announced." },
  { icon: FileLock2, label: "Records stay sealed", desc: "Case notes are never shared with family, employers or anyone — without your written consent." },
  { icon: Lock, label: "Solo or couple, your call", desc: "You decide who joins the consultation, and what is discussed with them." },
  { icon: ShieldCheck, label: "Qualified doctors only", desc: "Consultations by MD Neuropsychiatry specialists — not unqualified 'clinics' or hakims." },
];

const myths = [
  { m: "\"Nightfall makes you weak.\"", f: "Nocturnal emission is a normal physiological event. It causes no weakness, no infertility, no loss of strength." },
  { m: "\"Masturbation causes permanent damage.\"", f: "There is no scientific evidence of physical harm. The distress usually comes from guilt, not the act." },
  { m: "\"Erectile problems mean I'm finished.\"", f: "Over 90% of erectile dysfunction is treatable — often the first sign of a manageable medical issue like diabetes." },
  { m: "\"Only men need sexual treatment.\"", f: "Sexual difficulties are equally common in women, and equally treatable. Silence, not biology, is the barrier." },
];

export default function Sexology() {
  useSEO({
    title: "Sexologist in Bhavnagar · Sex Therapy & Psychosexual Clinic",
    description: "Confidential sexology and psychosexual medicine at Manas Hospital, Bhavnagar. Treatment for erectile dysfunction, premature ejaculation, low libido, vaginismus, Dhat syndrome, unconsummated marriage and couple intimacy concerns. By Drs. Kamlesh & Dharmin Shah.",
    image: HERO,
    lang: "en",
    alternates: [
      { hreflang: "en-IN", href: "https://manasneuropsychiatryhospital.in/sexology" },
      { hreflang: "gu-IN", href: "https://manasneuropsychiatryhospital.in/gu/sexology" },
      { hreflang: "x-default", href: "https://manasneuropsychiatryhospital.in/sexology" },
    ],
  });
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  return (
    <div data-testid="sexology-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <p className="eyebrow text-[#A39171] mb-6">{t("Sexology & Psychosexual Medicine")}</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-5xl">
          {t("The hardest part is")} <br /><span className="italic text-[#E07A5F]">{t("saying it out loud")}</span>.
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-2xl leading-relaxed">
          {t("A dedicated sex therapy clinic within a psychiatric hospital — where sexual health is treated as medicine, not taboo. Private consultations, accurate answers, and evidence-based treatment for men, women and couples.")}
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button data-testid="sx-hero-book" onClick={() => setOpen(true)} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
            {t("Book a Private Consultation")} <ArrowUpRight className="ml-1 w-4 h-4" />
          </Button>
          <a href="tel:+919427560101" data-testid="sx-hero-call" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-colors">
            {t("Speak to us discreetly")}
          </a>
        </div>
        <Link
          to="/gu/sexology"
          data-testid="sx-switch-gujarati"
          className="mt-6 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-[#A39171]/40 text-[#5C524E] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
        >
          <Languages className="w-4 h-4" /> ગુજરાતીમાં વાંચો
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] relative">
          <img src={HERO} alt="Couple in a supportive consultation" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2D2623]/65 via-[#2D2623]/10 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:max-w-lg text-white">
            <p className="eyebrow text-[#E2C044] mb-3">{t("Since 1989 · Bhavnagar")}</p>
            <p className="font-display text-2xl md:text-3xl leading-tight">
              {t("Two generations of psychiatrists. One quiet room. Zero judgement.")}
            </p>
          </div>
        </div>
      </section>

      {/* Confidentiality */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10">
              <div className="md:col-span-5">
                <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center mb-6">
                  <Lock className="w-5 h-5" />
                </span>
                <p className="eyebrow text-[#E2C044] mb-4">{t("100% Confidential")}</p>
                <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
                  {t("What is said here")} <span className="italic text-[#E07A5F]">{t("stays here")}</span>.
                </h2>
                <p className="text-[#FDFBF7]/75 mt-6 leading-relaxed">
                  {t("Every sexology consultation happens in a separate, closed cabin with a dedicated time slot — so you never have to explain yourself to anyone else in the clinic.")}
                </p>
              </div>
              <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {promises.map((p, i) => (
                  <div key={p.label} data-testid={`sx-privacy-${i}`}
                    className="bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 rounded-2xl p-6 hover:bg-[#FDFBF7]/10 transition-colors">
                    <p.icon className="w-5 h-5 text-[#E2C044] mb-4" />
                    <h3 className="font-display text-xl leading-tight">{t(p.label)}</h3>
                    <p className="text-sm text-[#FDFBF7]/70 mt-2 leading-relaxed">{t(p.desc)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions we treat */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-[#A39171] mb-4">{t("Psychosexual Problems We Treat")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              {t("Nothing you bring here")} <span className="italic text-[#E07A5F]">{t("is unusual to us")}</span>.
            </h2>
            <p className="mt-6 text-lg text-[#5C524E] leading-relaxed">
              {t("Sexual difficulties affect nearly one in three adults at some point. Here is the spectrum our clinic treats — for individuals and for couples.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {groups.map((g, gi) => (
              <div key={g.label} data-testid={`sx-group-${gi}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 md:p-9 border border-[#A39171]/15 hover:shadow-[0_20px_50px_rgba(163,145,113,0.12)] transition-shadow">
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-display text-3xl text-[#E07A5F]">0{gi + 1}</span>
                  <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight">{t(g.label)}</h3>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-[15px] text-[#5C524E]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#E2C044] mt-2 shrink-0" />
                      {t(it)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-[#A39171] mb-4">{t("How We Treat")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              {t("Facts first.")} <span className="italic text-[#E07A5F]">{t("Then therapy. Then medicine")}</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {approach.map((a, i) => (
              <div key={a.title} data-testid={`sx-approach-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-8 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all">
                <span className="w-12 h-12 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center mb-5">
                  <a.icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-2xl text-[#2D2623] tracking-tight leading-tight">{t(a.title)}</h3>
                <p className="text-[#5C524E] mt-3 text-sm leading-relaxed">{t(a.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Myth vs Fact */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">{t("Myth & Medicine")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            {t("Half the cure is")} <span className="italic text-[#E07A5F]">{t("correct information")}</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {myths.map((x, i) => (
              <div key={i} data-testid={`sx-myth-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15">
                <p className="font-display text-xl md:text-2xl text-[#A39171] italic leading-snug">{t(x.m)}</p>
                <div className="w-10 h-[2px] bg-[#E07A5F] my-5" />
                <p className="text-[#2D2623] leading-relaxed">{t(x.f)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* First visit journey */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">What to expect</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14">Your first visit.</h2>
          <ol className="space-y-10">
            {[
              { t: "Step 1 — A private slot", d: "You are given a dedicated appointment time so the cabin is yours alone. Walk-ins are also accommodated discreetly." },
              { t: "Step 2 — The conversation", d: "A detailed, unhurried sexual and medical history. You share only what you are ready to share — we ask the rest gently." },
              { t: "Step 3 — Examination & tests, if needed", d: "A relevant physical examination and basic blood work where clinically indicated. Nothing is done without your consent." },
              { t: "Step 4 — Your plan, explained", d: "A written plan combining therapy, exercises and medication where required — with a realistic timeline, not false promises." },
              { t: "Step 5 — Follow-up & review", d: "Progress is reviewed every 2–4 weeks. Most psychosexual conditions improve substantially within 6–12 weeks." },
            ].map((s, i) => (
              <li key={s.t} data-testid={`sx-step-${i}`} className="grid grid-cols-12 gap-6 border-b border-[#A39171]/15 pb-10 last:border-none">
                <div className="col-span-12 md:col-span-3">
                  <span className="font-display text-4xl text-[#E07A5F]">0{i + 1}</span>
                </div>
                <div className="col-span-12 md:col-span-9">
                  <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight">{s.t}</h3>
                  <p className="text-[#5C524E] mt-3 text-lg">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Anonymous recovery stories */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <StoryCarousel
            category="sexology"
            eyebrow={t("Quietly, it gets better")}
            heading={
              <>
                {t("People who sat")} <span className="italic text-[#E07A5F]">{t("exactly where you are")}</span>.
              </>
            }
            note={t("Anonymous accounts from patients treated at our sex therapy clinic. No names, no details that could identify anyone — only what changed for them.")}
          />
        </div>
      </section>

      {/* Questions patients hesitate to ask */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">{t("Questions you may hesitate to ask")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight mb-6">
            {t("The things patients ask us")} <span className="italic text-[#E07A5F]">{t("only after the door closes")}</span>.
          </h2>
          <p className="text-lg text-[#5C524E] leading-relaxed mb-14 max-w-2xl">
            {t("We have answered them here, plainly — so you don't have to ask them out loud unless you want to.")}
          </p>
          <Accordion type="single" collapsible className="space-y-3">
            {shyFaqs.map((it, i) => (
              <AccordionItem
                key={it.q}
                value={`shy-${i}`}
                data-testid={`sx-faq-${i}`}
                className="bg-[#FDFBF7] rounded-2xl border border-[#A39171]/15 px-6 data-[state=open]:border-[#E07A5F]/35 transition-colors"
              >
                <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-[#2D2623] tracking-tight hover:no-underline py-5">
                  {t(it.q)}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C524E] text-base md:text-lg leading-relaxed pb-6">
                  {t(it.a)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Anonymous enquiry */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <AnonymousEnquiry t={t} />
        </div>
      </section>

      {/* Note for partners */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-[#E07A5F] text-white rounded-3xl p-8 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-20 pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <Sparkles className="w-6 h-6 text-[#2D2623] mb-5" />
                <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
                  {t("Coming as a couple doubles the outcome.")}
                </h2>
                <p className="text-white/85 mt-5 leading-relaxed max-w-2xl">
                  {t("Sexual concerns are rarely one person's problem. When partners attend even one session together, treatment tends to be faster, kinder and far more likely to last. If your partner isn't ready yet — come alone. We'll start anyway.")}
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Button data-testid="sx-couple-book" onClick={() => setOpen(true)}
                  className="rounded-full bg-[#2D2623] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-[#FDFBF7] px-8 h-12 font-medium">
                  {t("Book Couple Session")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center relative">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
            {t("You've waited long enough.")} <span className="italic text-[#E07A5F]">{t("One visit is enough to begin")}</span>.
          </h2>
          <p className="text-[#FDFBF7]/70 mt-5 max-w-xl mx-auto">
            {t("Private consultation with a qualified neuropsychiatrist & sexologist. Complete confidentiality assured.")}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="sx-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">{t("Request Appointment")}</Button>
            <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">{t("Call")} +91 94275 60101</a>
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
